/**
 * <FilterPanel />
 *
 * Marketplace filter controls. Rendered inline on desktop (left column)
 * and inside a <details> drawer on mobile so seniors aren't hunting for
 * the filter button.
 */
import type { CategoryNode, ProductCondition } from "../../lib/types";
import { Select } from "../ui/Select";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { cn } from "../../lib/cn";

export interface MarketplaceFilters {
  category_id: string;
  condition: ProductCondition | "";
  min_price: string;
  max_price: string;
  featured_only: boolean;
  /** Phase 15 — "Make / Model" free text. Backend matches on
   *  fitment_tags OR compatibility_notes (case-insensitive). */
  fitment: string;
}

export const EMPTY_FILTERS: MarketplaceFilters = {
  category_id: "",
  condition: "",
  min_price: "",
  max_price: "",
  featured_only: false,
  fitment: "",
};

interface Props {
  filters: MarketplaceFilters;
  categories: CategoryNode[];
  onChange: (next: MarketplaceFilters) => void;
  onApply: () => void;
  /** Layout: `inline` on desktop, `drawer` in the mobile <details>. */
  layout?: "inline" | "drawer";
}

export function FilterPanel({
  filters,
  categories,
  onChange,
  onApply,
  layout = "inline",
}: Props) {
  const set = <K extends keyof MarketplaceFilters>(key: K, v: MarketplaceFilters[K]) =>
    onChange({ ...filters, [key]: v });

  const reset = () => onChange(EMPTY_FILTERS);

  // Namespace test ids by layout so the twin mobile/desktop panels don't
  // clash when an automation tool searches for a generic selector.
  const tid = (name: string) => `filter-${name}-${layout}`;

  return (
    <div
      data-testid={`filter-panel-${layout}`}
      className={cn(
        "flex flex-col gap-4 bg-white rounded-2xl border border-surface-border p-5",
        layout === "inline" && "sticky top-24",
      )}
    >
      <h2 className="text-lg font-bold">Filters</h2>

      <Select
        label="Category"
        value={filters.category_id}
        onChange={(e) => set("category_id", e.target.value)}
        placeholder="All categories"
        data-testid={tid("category")}
      >
        {categories.map((cat) =>
          cat.children.length === 0 ? (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ) : (
            // Parent brand — non-selectable group header. No <option> for the
            // parent itself: `category_id` matches products by exact id, and
            // no product ever carries a brand-section id (only childless
            // parents or year-range children), so a selectable parent option
            // would just be a filter that always returns zero results.
            <optgroup key={cat.id} label={cat.name.toUpperCase()}>
              {cat.children.map((child) => (
                <option key={child.id} value={child.id}>
                  {child.name}
                </option>
              ))}
            </optgroup>
          ),
        )}
      </Select>

      <Input
        label="Make / Model"
        value={filters.fitment}
        onChange={(e) => set("fitment", e.target.value)}
        placeholder="e.g. Mercury 50hp 1998"
        hint="Matches fitment tags or compatibility notes on listings."
        data-testid={tid("fitment")}
      />

      <Select
        label="Condition"
        value={filters.condition}
        onChange={(e) => set("condition", e.target.value as ProductCondition | "")}
        placeholder="Any condition"
        options={[
          { value: "NEW", label: "New" },
          { value: "USED", label: "Used" },
          { value: "REFURBISHED", label: "Refurbished" },
        ]}
        data-testid={tid("condition")}
      />

      <div
        className={cn(
          "grid gap-3 min-w-0",
          layout === "drawer" ? "grid-cols-2" : "grid-cols-1",
        )}
      >
        <Input
          label="Min price"
          type="number"
          min="0"
          step="0.01"
          placeholder="$0"
          value={filters.min_price}
          onChange={(e) => set("min_price", e.target.value)}
          data-testid={tid("min-price")}
        />
        <Input
          label="Max price"
          type="number"
          min="0"
          step="0.01"
          placeholder="No limit"
          value={filters.max_price}
          onChange={(e) => set("max_price", e.target.value)}
          data-testid={tid("max-price")}
        />
      </div>

      <label className="flex items-center gap-3 cursor-pointer select-none mt-1">
        <input
          type="checkbox"
          checked={filters.featured_only}
          onChange={(e) => set("featured_only", e.target.checked)}
          className="h-5 w-5 accent-brand"
          data-testid={tid("featured-only")}
        />
        <span className="font-semibold text-ink">Featured listings only</span>
      </label>

      <div className="flex flex-col sm:flex-row items-stretch gap-2 pt-2">
        <Button variant="primary" onClick={onApply} fullWidth data-testid={tid("apply")}>
          Apply filters
        </Button>
        <Button variant="outline" onClick={reset} data-testid={tid("reset")}>
          Reset
        </Button>
      </div>
    </div>
  );
}
